import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { LogOut, ShoppingBag, Mail, ExternalLink, Bell, Star, Send, Trash2, BarChart3, Trophy } from "lucide-react";

const SHOPIFY_ADMIN = `https://${process.env.REACT_APP_SHOPIFY_DOMAIN}/admin`;

export default function AdminDashboard() {
    const [tab, setTab] = useState("subscribers");
    const [orders, setOrders] = useState([]);
    const [subs, setSubs] = useState([]);
    const [pushSubs, setPushSubs] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [notifyForm, setNotifyForm] = useState({
        product_handle: "",
        title: "",
        body: "",
    });
    const [notifyLoading, setNotifyLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/admin/me")
            .then(refresh)
            .catch(() => {
                localStorage.removeItem("admin_token");
                navigate("/admin");
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function refresh() {
        api.get("/admin/orders")
            .then((r) => setOrders(r.data))
            .catch(() => {});
        api.get("/admin/newsletter")
            .then((r) => setSubs(r.data))
            .catch(() => {});
        api.get("/admin/push/subscribers")
            .then((r) => setPushSubs(r.data))
            .catch(() => {});
        api.get("/admin/reviews")
            .then((r) => setReviews(r.data))
            .catch(() => {});
        api.get("/admin/analytics/summary")
            .then((r) => setAnalytics(r.data))
            .catch(() => {});
    }

    function logout() {
        localStorage.removeItem("admin_token");
        navigate("/admin");
    }

    async function sendNotification(e) {
        e.preventDefault();
        if (!notifyForm.product_handle) {
            toast.error("Inserisci l'handle del prodotto");
            return;
        }
        setNotifyLoading(true);
        try {
            const { data } = await api.post("/admin/push/notify", {
                product_handle: notifyForm.product_handle,
                title: notifyForm.title || null,
                body: notifyForm.body || null,
            });
            toast.success(`Inviate ${data.sent}/${data.total} notifiche`);
            setNotifyForm({ product_handle: "", title: "", body: "" });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Errore invio";
            toast.error(typeof msg === "string" ? msg : "Errore");
        } finally {
            setNotifyLoading(false);
        }
    }

    async function toggleReview(id, approved) {
        try {
            await api.patch(`/admin/reviews/${id}?approved=${approved}`);
            toast.success(approved ? "Approvata" : "Nascosta");
            refresh();
        } catch {
            toast.error("Errore");
        }
    }

    async function deleteReview(id) {
        if (!window.confirm("Eliminare recensione?")) return;
        try {
            await api.delete(`/admin/reviews/${id}`);
            toast.success("Eliminata");
            refresh();
        } catch {
            toast.error("Errore");
        }
    }

    return (
        <div
            className="max-w-7xl mx-auto px-5 lg:px-10 py-10"
            data-testid="admin-dashboard-page"
        >
            <div className="flex flex-wrap items-center justify-between gap-4 border-b-2 border-ink pb-6 mb-8">
                <div>
                    <div className="font-hand text-2xl text-bounty">
                        pannello capitano
                    </div>
                    <h1 className="font-display text-5xl tracking-tight">
                        DASHBOARD
                    </h1>
                </div>
                <div className="flex gap-3">
                    <a
                        href={SHOPIFY_ADMIN}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-manga btn-manga-red"
                        data-testid="admin-shopify-link"
                    >
                        Shopify Admin
                        <ExternalLink size={14} className="ml-2" />
                    </a>
                    <button
                        onClick={logout}
                        className="btn-manga"
                        data-testid="admin-logout-btn"
                    >
                        <LogOut size={16} className="mr-2" /> Esci
                    </button>
                </div>
            </div>

            <div className="bounty-card p-5 mb-8 bg-gold/30">
                <div className="font-hand text-xl text-bounty">
                    Store gestito da Shopify
                </div>
                <p className="text-sm mt-1 text-ink/80">
                    I <span className="font-bold">prodotti</span> e gli{" "}
                    <span className="font-bold">ordini</span> si gestiscono
                    direttamente dal pannello Shopify. Questa dashboard mostra
                    solo gli <span className="font-bold">iscritti newsletter</span>{" "}
                    (custom lato server) e le vecchie transazioni Stripe (se
                    presenti).
                </p>
            </div>

            <div className="flex gap-3 mb-6 flex-wrap">
                {[
                    {
                        k: "subscribers",
                        label: "Newsletter",
                        Icon: Mail,
                        count: subs.length,
                    },
                    {
                        k: "analytics",
                        label: "Analytics",
                        Icon: BarChart3,
                        count: analytics?.totals?.pirates || 0,
                    },
                    {
                        k: "push",
                        label: "Notifiche Push",
                        Icon: Bell,
                        count: pushSubs.length,
                    },
                    {
                        k: "reviews",
                        label: "Recensioni",
                        Icon: Star,
                        count: reviews.length,
                    },
                    {
                        k: "orders",
                        label: "Ordini Legacy",
                        Icon: ShoppingBag,
                        count: orders.length,
                    },
                ].map(({ k, label, Icon, count }) => (
                    <button
                        key={k}
                        onClick={() => setTab(k)}
                        className={`flex items-center gap-2 px-4 py-2 border-2 border-ink font-display tracking-widest text-lg ${tab === k ? "bg-ink text-parchment shadow-manga-red" : "bg-white shadow-manga"}`}
                        data-testid={`admin-tab-${k}`}
                    >
                        <Icon size={16} /> {label}{" "}
                        <span className="font-mono text-xs">({count})</span>
                    </button>
                ))}
            </div>

            {tab === "subscribers" && (
                <div className="overflow-x-auto bounty-card">
                    <table
                        className="w-full text-left"
                        data-testid="admin-subs-table"
                    >
                        <thead className="bg-ink text-parchment">
                            <tr>
                                <th className="p-3 font-display tracking-wider text-sm">
                                    Email
                                </th>
                                <th className="p-3 font-display tracking-wider text-sm">
                                    Codice
                                </th>
                                <th className="p-3 font-display tracking-wider text-sm">
                                    Sconto
                                </th>
                                <th className="p-3 font-display tracking-wider text-sm">
                                    Usato
                                </th>
                                <th className="p-3 font-display tracking-wider text-sm">
                                    Data
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {subs.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan="5"
                                        className="p-8 text-center font-hand text-2xl text-ink/40"
                                    >
                                        nessun iscritto ancora
                                    </td>
                                </tr>
                            ) : (
                                subs.map((s) => (
                                    <tr
                                        key={s.email}
                                        className="border-t border-ink/10"
                                    >
                                        <td className="p-3 text-sm">
                                            {s.email}
                                        </td>
                                        <td className="p-3 font-mono text-xs text-bounty">
                                            {s.discount_code}
                                        </td>
                                        <td className="p-3 font-mono">
                                            {s.percent}%
                                        </td>
                                        <td className="p-3 font-mono text-xs">
                                            {s.used ? "✓ usato" : "—"}
                                        </td>
                                        <td className="p-3 font-mono text-xs">
                                            {new Date(
                                                s.created_at
                                            ).toLocaleString("it-IT")}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === "analytics" && analytics && (
                <div className="space-y-6" data-testid="admin-analytics-tab">
                    {/* Totals cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                        {[
                            { k: "Pirati", v: analytics.totals.pirates, color: "bg-white" },
                            { k: "Eventi", v: analytics.totals.events, color: "bg-white" },
                            { k: "Newsletter", v: analytics.totals.newsletter_signups, color: "bg-seafoam" },
                            { k: "Checkout click", v: analytics.totals.checkout_clicks, color: "bg-gold" },
                            { k: "Acquisti", v: analytics.totals.purchases, color: "bg-bounty text-parchment" },
                        ].map((s) => (
                            <div key={s.k} className={`bounty-card p-4 ${s.color}`}>
                                <div className="font-hand text-xs opacity-70">{s.k}</div>
                                <div className="font-display text-3xl leading-none mt-1">{s.v}</div>
                            </div>
                        ))}
                    </div>

                    {/* Level distribution */}
                    <div className="bounty-card p-5">
                        <div className="font-display text-2xl tracking-tight mb-4 flex items-center gap-2">
                            <Trophy size={20} className="text-bounty" />
                            DISTRIBUZIONE LIVELLI
                        </div>
                        {analytics.level_distribution.length === 0 ? (
                            <div className="font-hand text-xl text-ink/40">Nessun dato ancora</div>
                        ) : (
                            <div className="space-y-2">
                                {(() => {
                                    const max = Math.max(...analytics.level_distribution.map((l) => l.count), 1);
                                    return analytics.level_distribution.map((l) => (
                                        <div key={l.level} className="flex items-center gap-3">
                                            <div className="w-8 font-display text-lg text-bounty">{l.level}</div>
                                            <div className="w-32 font-mono text-xs truncate">{l.name}</div>
                                            <div className="flex-1 h-6 border-2 border-ink bg-white overflow-hidden">
                                                <div
                                                    className="h-full bg-bounty"
                                                    style={{ width: `${(l.count / max) * 100}%` }}
                                                />
                                            </div>
                                            <div className="w-12 font-mono text-sm text-right font-bold">{l.count}</div>
                                        </div>
                                    ));
                                })()}
                            </div>
                        )}
                    </div>

                    {/* Conversion by level */}
                    <div className="bounty-card p-5">
                        <div className="font-display text-2xl tracking-tight mb-4">
                            CONVERSION RATE PER LIVELLO
                        </div>
                        {analytics.conversion_by_level.length === 0 ? (
                            <div className="font-hand text-xl text-ink/40">Nessun dato ancora</div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b-2 border-ink">
                                        <th className="text-left p-2 font-display tracking-wider">Livello</th>
                                        <th className="text-right p-2 font-display tracking-wider">Pirati</th>
                                        <th className="text-right p-2 font-display tracking-wider">Checkout click</th>
                                        <th className="text-right p-2 font-display tracking-wider">Acquisti</th>
                                        <th className="text-right p-2 font-display tracking-wider">Conv %</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {analytics.conversion_by_level.map((c) => (
                                        <tr key={c.level} className="border-t border-ink/10">
                                            <td className="p-2 font-mono">Lv{c.level} · {c.name}</td>
                                            <td className="p-2 text-right font-mono">{c.cohort}</td>
                                            <td className="p-2 text-right font-mono">{c.checkout_clicks}</td>
                                            <td className="p-2 text-right font-mono">{c.purchases}</td>
                                            <td className="p-2 text-right font-mono font-bold text-bounty">{c.conversion_rate}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Top pirates */}
                    <div className="bounty-card p-5">
                        <div className="font-display text-2xl tracking-tight mb-4 flex items-center gap-2">
                            <Trophy size={20} className="text-gold" />
                            TOP 15 PIRATI
                        </div>
                        {analytics.top_pirates.length === 0 ? (
                            <div className="font-hand text-xl text-ink/40">Nessun pirata ancora</div>
                        ) : (
                            <table className="w-full text-sm" data-testid="admin-top-pirates">
                                <thead>
                                    <tr className="border-b-2 border-ink">
                                        <th className="text-left p-2 font-display tracking-wider">#</th>
                                        <th className="text-left p-2 font-display tracking-wider">Device</th>
                                        <th className="text-left p-2 font-display tracking-wider">Livello</th>
                                        <th className="text-right p-2 font-display tracking-wider">XP</th>
                                        <th className="text-right p-2 font-display tracking-wider">Berry</th>
                                        <th className="text-right p-2 font-display tracking-wider">Ultima visita</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {analytics.top_pirates.map((p, i) => (
                                        <tr key={i} className="border-t border-ink/10">
                                            <td className="p-2 font-display text-lg">{i + 1}</td>
                                            <td className="p-2 font-mono text-xs">{p.device_id}</td>
                                            <td className="p-2 font-mono text-xs">Lv{p.level} {p.level_name}</td>
                                            <td className="p-2 text-right font-mono font-bold">{p.xp}</td>
                                            <td className="p-2 text-right font-mono text-bounty">{p.berry}</td>
                                            <td className="p-2 text-right font-mono text-xs text-ink/60">
                                                {p.last_seen ? new Date(p.last_seen).toLocaleDateString("it-IT") : "—"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {tab === "push" && (
                <div className="space-y-6">
                    <form
                        onSubmit={sendNotification}
                        className="bounty-card p-6 space-y-3"
                        data-testid="admin-notify-form"
                    >
                        <div className="font-display text-2xl tracking-tight">
                            INVIA NOTIFICA PUSH
                        </div>
                        <div className="text-xs font-mono text-ink/60">
                            Avvisa tutti gli iscritti a un determinato prodotto (per handle Shopify)
                        </div>
                        <div className="grid sm:grid-cols-3 gap-3">
                            <input
                                type="text"
                                required
                                placeholder="product_handle *"
                                value={notifyForm.product_handle}
                                onChange={(e) =>
                                    setNotifyForm({
                                        ...notifyForm,
                                        product_handle: e.target.value,
                                    })
                                }
                                className="input-manga text-sm"
                                data-testid="notify-handle-input"
                            />
                            <input
                                type="text"
                                placeholder="Titolo (opzionale)"
                                value={notifyForm.title}
                                onChange={(e) =>
                                    setNotifyForm({
                                        ...notifyForm,
                                        title: e.target.value,
                                    })
                                }
                                className="input-manga text-sm sm:col-span-2"
                            />
                        </div>
                        <textarea
                            rows={2}
                            placeholder="Messaggio (opzionale, ha un default)"
                            value={notifyForm.body}
                            onChange={(e) =>
                                setNotifyForm({
                                    ...notifyForm,
                                    body: e.target.value,
                                })
                            }
                            className="input-manga text-sm resize-none"
                        />
                        <button
                            type="submit"
                            disabled={notifyLoading}
                            className="btn-manga btn-manga-red"
                            data-testid="admin-notify-send-btn"
                        >
                            <Send size={14} className="mr-2" />
                            {notifyLoading ? "Invio..." : "Invia notifica"}
                        </button>
                    </form>

                    <div className="overflow-x-auto bounty-card">
                        <table
                            className="w-full text-left"
                            data-testid="admin-push-table"
                        >
                            <thead className="bg-ink text-parchment">
                                <tr>
                                    <th className="p-3 font-display tracking-wider text-sm">Prodotto</th>
                                    <th className="p-3 font-display tracking-wider text-sm">Handle</th>
                                    <th className="p-3 font-display tracking-wider text-sm">Email</th>
                                    <th className="p-3 font-display tracking-wider text-sm">Iscritto il</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pushSubs.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="p-8 text-center font-hand text-2xl text-ink/40">
                                            nessun iscritto push
                                        </td>
                                    </tr>
                                ) : (
                                    pushSubs.map((s, i) => (
                                        <tr key={i} className="border-t border-ink/10">
                                            <td className="p-3 text-sm">{s.product_title || "—"}</td>
                                            <td className="p-3 font-mono text-xs">{s.product_handle || "—"}</td>
                                            <td className="p-3 text-xs">{s.email || "—"}</td>
                                            <td className="p-3 font-mono text-xs">{new Date(s.created_at).toLocaleString("it-IT")}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {tab === "reviews" && (
                <div className="overflow-x-auto bounty-card">
                    <table
                        className="w-full text-left"
                        data-testid="admin-reviews-table"
                    >
                        <thead className="bg-ink text-parchment">
                            <tr>
                                <th className="p-3 font-display tracking-wider text-sm">Prodotto</th>
                                <th className="p-3 font-display tracking-wider text-sm">Voto</th>
                                <th className="p-3 font-display tracking-wider text-sm">Autore</th>
                                <th className="p-3 font-display tracking-wider text-sm">Titolo</th>
                                <th className="p-3 font-display tracking-wider text-sm">Stato</th>
                                <th className="p-3 font-display tracking-wider text-sm">Az.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reviews.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="p-8 text-center font-hand text-2xl text-ink/40">
                                        nessuna recensione ancora
                                    </td>
                                </tr>
                            ) : (
                                reviews.map((r) => (
                                    <tr key={r.id} className="border-t border-ink/10">
                                        <td className="p-3 font-mono text-xs">{r.product_handle}</td>
                                        <td className="p-3 font-mono">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</td>
                                        <td className="p-3 text-sm">{r.author}</td>
                                        <td className="p-3 text-sm">{r.title}</td>
                                        <td className="p-3">
                                            <span className={`px-2 py-1 text-xs font-mono ${r.approved ? "bg-bounty text-parchment" : "bg-gold text-ink"}`}>
                                                {r.approved ? "visibile" : "nascosta"}
                                            </span>
                                        </td>
                                        <td className="p-3">
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => toggleReview(r.id, !r.approved)}
                                                    className="px-2 py-1 border-2 border-ink text-xs bg-white hover:bg-seafoam"
                                                    data-testid={`review-toggle-${r.id}`}
                                                >
                                                    {r.approved ? "Nascondi" : "Approva"}
                                                </button>
                                                <button
                                                    onClick={() => deleteReview(r.id)}
                                                    className="w-8 h-8 border-2 border-ink bg-white hover:bg-bounty hover:text-parchment inline-flex items-center justify-center"
                                                    data-testid={`review-delete-${r.id}`}
                                                >
                                                    <Trash2 size={12} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === "orders" && (
                <div className="overflow-x-auto bounty-card">
                    <table
                        className="w-full text-left"
                        data-testid="admin-orders-table"
                    >
                        <thead className="bg-ink text-parchment">
                            <tr>
                                <th className="p-3 font-display tracking-wider text-sm">
                                    Order ID
                                </th>
                                <th className="p-3 font-display tracking-wider text-sm">
                                    Email
                                </th>
                                <th className="p-3 font-display tracking-wider text-sm">
                                    Totale
                                </th>
                                <th className="p-3 font-display tracking-wider text-sm">
                                    Stato
                                </th>
                                <th className="p-3 font-display tracking-wider text-sm">
                                    Data
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan="5"
                                        className="p-8 text-center font-hand text-2xl text-ink/40"
                                    >
                                        gli ordini nuovi vivono in Shopify
                                    </td>
                                </tr>
                            ) : (
                                orders.map((o) => (
                                    <tr
                                        key={o.order_id}
                                        className="border-t border-ink/10"
                                    >
                                        <td className="p-3 font-mono text-xs">
                                            {o.order_id.slice(0, 8)}
                                        </td>
                                        <td className="p-3 text-xs">
                                            {o.customer_email || "—"}
                                        </td>
                                        <td className="p-3 font-mono">
                                            €{o.amount}
                                        </td>
                                        <td className="p-3">
                                            <span className="px-2 py-1 bg-bounty text-parchment text-xs font-mono">
                                                {o.status}
                                            </span>
                                        </td>
                                        <td className="p-3 font-mono text-xs">
                                            {new Date(
                                                o.created_at
                                            ).toLocaleString("it-IT")}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
